import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Divider,
  alpha,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
} from "@mui/material";
import {
  ExitToApp,
  Save,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  Close,
  AccessTime,
  Description,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";

const CloseCycleDialog = ({
  open,
  onClose,
  onConfirm,
  selectedRecord,
  isSubmitting,
  submitStatus,
  statusMessage,
}) => {
  const [observation, setObservation] = useState("");
  const [exitDate, setExitDate] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Resetear formulario cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      // Establecer fecha actual + 1 minuto en formato local
      const now = new Date();
      const minDate = new Date(now.getTime() + 60000);
      // Formatear en local sin usar toISOString
      const year = minDate.getFullYear();
      const month = String(minDate.getMonth() + 1).padStart(2, "0");
      const day = String(minDate.getDate()).padStart(2, "0");
      const hours = String(minDate.getHours()).padStart(2, "0");
      const minutes = String(minDate.getMinutes()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

      setObservation("");
      setExitDate(formattedDate);
      setErrors({});
      setTouched({});
    }
  }, [open]);

  // Obtener fecha mínima (entrada + 1 minuto) en formato local
  const getMinExitDate = () => {
    if (!selectedRecord?.enter_at) return "";
    const enterDate = new Date(selectedRecord.enter_at);
    const minDate = new Date(enterDate.getTime() + 60000);
    // Formatear en local sin usar toISOString
    const year = minDate.getFullYear();
    const month = String(minDate.getMonth() + 1).padStart(2, "0");
    const day = String(minDate.getDate()).padStart(2, "0");
    const hours = String(minDate.getHours()).padStart(2, "0");
    const minutes = String(minDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const validateObservation = (value) => {
    if (!value || value.trim() === "") {
      return "La observación es requerida";
    }
    if (value.trim().length < 5) {
      return "La observación debe tener al menos 5 caracteres";
    }
    return "";
  };

  const validateExitDate = (value) => {
    if (!value) {
      return "La fecha de salida es requerida";
    }
    if (!selectedRecord?.enter_at) {
      return "No se puede validar la fecha de entrada";
    }
    const enterDate = new Date(selectedRecord.enter_at);
    const exitDateObj = new Date(value);
    if (exitDateObj <= enterDate) {
      return "La fecha de salida debe ser mayor a la fecha de entrada";
    }
    return "";
  };

  const handleObservationChange = (e) => {
    const value = e.target.value;
    setObservation(value);
    setTouched((prev) => ({ ...prev, observation: true }));
    const error = validateObservation(value);
    setErrors((prev) => ({ ...prev, observation: error }));
  };

  const handleExitDateChange = (e) => {
    const value = e.target.value;
    setExitDate(value);
    setTouched((prev) => ({ ...prev, exitDate: true }));
    const error = validateExitDate(value);
    setErrors((prev) => ({ ...prev, exitDate: error }));
  };

  const handleConfirm = () => {
    const obsError = validateObservation(observation);
    const dateError = validateExitDate(exitDate);

    setErrors({
      observation: obsError,
      exitDate: dateError,
    });
    setTouched({
      observation: true,
      exitDate: true,
    });

    if (obsError || dateError) {
      return;
    }

    // Enviar la fecha en el formato correcto (datetime-local)
    onConfirm(observation, exitDate);
  };

  const isFormValid = () => {
    const obsValid = observation.trim() !== "" && observation.trim().length >= 5;
    const dateValid = exitDate !== "" && !validateExitDate(exitDate);
    return obsValid && dateValid;
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const isFormComplete = () => {
    return observation.trim() !== "" && observation.trim().length >= 5 && exitDate !== "";
  };

  const minExitDate = getMinExitDate();
  const isExitDateDisabled = !selectedRecord?.enter_at;

  // Formatear fecha de entrada para mostrar en la zona horaria local
  const formatEnterDate = () => {
    if (!selectedRecord?.enter_at) return "N/A";
    return new Date(selectedRecord.enter_at).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  // Formatear fecha de salida para mostrar en el resumen en zona horaria local
  const formatExitDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : (
            <ExitToApp sx={{ mr: 2, fontSize: "1.75rem", color: "warning.main" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Cerrar Ciclo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete la información para cerrar el ciclo del vehículo
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Stack spacing={3}>
          {submitStatus && (
            <Alert
              severity={submitStatus === "success" ? "success" : "error"}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusMessage}
              </Typography>
            </Alert>
          )}

          {/* Información del vehículo */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Información del vehículo
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  PLACA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
                  {selectedRecord?.plate || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  HOST
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
                  {selectedRecord?.host || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  FECHA DE ENTRADA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
                  {formatEnterDate()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Chip
                  label="ACTIVO - SIN SALIDA"
                  size="small"
                  color="warning"
                  icon={<Warning />}
                  sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "26px" }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Formulario */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Grid container spacing={3}>
              {/* Fecha de Salida */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fecha de Salida *"
                  type="datetime-local"
                  value={exitDate}
                  onChange={handleExitDateChange}
                  error={isFieldError("exitDate")}
                  helperText={isFieldError("exitDate") && errors.exitDate}
                  disabled={isSubmitting || isExitDateDisabled}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTime sx={{ color: "#5c6bc0" }} />
                      </InputAdornment>
                    ),
                    inputProps: {
                      min: minExitDate,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                  }}
                />
                {selectedRecord?.enter_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    La fecha de salida debe ser mayor a: {formatEnterDate()}
                  </Typography>
                )}
              </Grid>

              {/* Observación */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observación *"
                  multiline
                  rows={4}
                  value={observation}
                  onChange={handleObservationChange}
                  error={isFieldError("observation")}
                  helperText={isFieldError("observation") && errors.observation}
                  disabled={isSubmitting}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <Description sx={{ color: "#5c6bc0" }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Describa el motivo del cierre del ciclo..."
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "1rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  * La observación es requerida y debe tener al menos 5 caracteres
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Resumen del Cierre */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: alpha("#ed6c02", 0.3),
              backgroundColor: alpha("#ed6c02", 0.02),
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
              RESUMEN DEL CIERRE
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  PLACA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                  {selectedRecord?.plate || "—"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  FECHA DE SALIDA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                  {exitDate ? formatExitDate(exitDate) : "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  OBSERVACIÓN
                </Typography>
                <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
                  {observation || "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Barra de progreso */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  COMPLETITUD DEL FORMULARIO
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
                  {[exitDate, observation].filter((v) => v && v.toString().trim() !== "").length}
                  /2 campos
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  ([exitDate, observation].filter((v) => v && v.toString().trim() !== "").length /
                    2) *
                  100
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha("#ed6c02", 0.1),
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#ed6c02",
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {isFormComplete() ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: alpha("#4caf50", 0.05),
                  border: "1px dashed",
                  borderColor: alpha("#4caf50", 0.3),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Todos los campos requeridos están completos.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: alpha("#ff9800", 0.05),
                  border: "1px dashed",
                  borderColor: alpha("#ff9800", 0.3),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Warning sx={{ color: "#ff9800", fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Complete todos los campos requeridos para continuar
                </Typography>
              </Box>
            )}
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: 1, borderColor: "divider", gap: 1 }}>
        {submitStatus === "success" ? (
          <MDButton
            variant="contained"
            color="success"
            onClick={onClose}
            startIcon={<CheckCircle />}
            sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
          >
            Aceptar
          </MDButton>
        ) : submitStatus === "error" ? (
          <>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cerrar
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => {
                onClose();
              }}
              startIcon={<Refresh />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cancelar
            </MDButton>
          </>
        ) : (
          <>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={handleConfirm}
              disabled={isSubmitting || !isFormValid()}
              startIcon={<Save />}
              sx={{ px: 4, minWidth: 200, borderRadius: "8px", fontWeight: "bold", height: "48px" }}
            >
              {isSubmitting ? "Cerrando ciclo..." : "CERRAR CICLO"}
            </MDButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

CloseCycleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedRecord: PropTypes.object,
  isSubmitting: PropTypes.bool,
  submitStatus: PropTypes.string,
  statusMessage: PropTypes.string,
};

CloseCycleDialog.defaultProps = {
  selectedRecord: null,
  isSubmitting: false,
  submitStatus: null,
  statusMessage: "",
};

export default CloseCycleDialog;
